'use client';

import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { useWindowSize } from 'usehooks-ts';
import { useArtifact } from '@/hooks/use-artifact';
import { useArtifactPanelWidth } from '@/hooks/use-artifact-panel-width';
import { useArtifactResponsive } from '@/hooks/use-artifact-responsive';

export type ArtifactSplitLayout = {
  isActive: boolean;
  leftPaneWidth: number;
  rightPaneWidth: number;
};

const ArtifactSplitLayoutContext = createContext<ArtifactSplitLayout | null>(
  null,
);

export function ArtifactSplitLayoutProvider({
  value,
  children,
}: {
  value: ArtifactSplitLayout;
  children?: ReactNode;
}) {
  return (
    <ArtifactSplitLayoutContext.Provider value={value}>
      {children}
    </ArtifactSplitLayoutContext.Provider>
  );
}

export function useArtifactSplitLayout(): ArtifactSplitLayout {
  const contextValue = useContext(ArtifactSplitLayoutContext);
  const { artifact } = useArtifact();
  const { isMobile } = useArtifactResponsive();
  const { width: windowWidth } = useWindowSize();
  const liveLeftPaneWidth = useArtifactPanelWidth();

  const fallbackValue = useMemo(() => {
    const leftPaneWidth = liveLeftPaneWidth ?? 0;
    const isActive = artifact.isVisible && !isMobile && leftPaneWidth > 0;

    return {
      isActive,
      leftPaneWidth,
      rightPaneWidth: isActive ? Math.max(windowWidth - leftPaneWidth, 0) : 0,
    };
  }, [artifact.isVisible, isMobile, liveLeftPaneWidth, windowWidth]);

  return contextValue ?? fallbackValue;
}
